import { Component, ReactNode } from 'react';

export interface ErrorBoundaryProps {
  renderError: (error: any, retry: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: any;
}

/**
 * Detects DOM manipulation errors caused by external interference
 * (browser extensions, translation tools, accessibility services, etc.)
 * that conflict with React's virtual DOM reconciliation.
 */
const isDomManipulationError = (error: any): boolean => {
  if (!(error instanceof Error)) return false;
  const message = error.message || '';
  return (
    message.includes('removeChild') ||
    message.includes('insertBefore') ||
    message.includes('appendChild') ||
    message.includes('The node to be removed is not a child of this node') ||
    message.includes("Failed to execute 'insertBefore' on 'Node'")
  );
};

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = {
    hasError: false,
    error: null
  };

  private autoRetryAttempted = false;

  componentDidCatch(error: any) {
    // Auto-retry once for DOM manipulation errors caused by external
    // interference (browser extensions, translate, accessibility services).
    // These are transient and typically resolve on a clean re-render.
    if (isDomManipulationError(error) && !this.autoRetryAttempted) {
      this.autoRetryAttempted = true;
      this.setState({ error: null, hasError: false });
      return;
    }

    this.setState({ error, hasError: true });
    /* eslint-disable-next-line no-console */
    console.error('Caught an unexpected unknown error!', error);
  }

  retry = () => {
    // Reset auto-retry flag on manual retry so the user gets another
    // automatic attempt if the same transient error occurs again.
    this.autoRetryAttempted = false;
    this.setState({
      error: null,
      hasError: false
    });
  };

  render() {
    const {
      state: { hasError, error },
      props: { renderError, children },
      retry
    } = this;

    return hasError ? renderError(error, retry) : children;
  }
}
